import React, { useState } from 'react';
import { toast } from 'sonner';
import { MapPin, Camera, Upload, Check, ExternalLink, Globe, User, Building2, ArrowLeft } from 'lucide-react';
import { uploadFile, srcForPath } from '@/lib/uploads';

// Group inspectors by Admin / Karaikudi Office / Sivagangai Office (matches HTML spec).
const groupInspectors = (staff) => {
  const admins = staff.filter((s) => s.role === 'admin');
  const kk = staff.filter((s) => s.role === 'staff' && s.office === 'Karaikudi');
  const svg = staff.filter((s) => s.role === 'staff' && s.office === 'Sivagangai');
  return [
    { key: 'admin', label: 'ADMIN', icon: User, isAdmin: true, items: admins },
    { key: 'kk', label: 'KARAIKUDI OFFICE', icon: Building2, items: kk },
    { key: 'svg', label: 'SIVAGANGAI OFFICE', icon: Building2, items: svg },
  ].filter((g) => g.items.length > 0);
};

const parseCoord = (raw) => {
  if (!raw) return null;
  const m = raw.trim().match(/(-?\d+(?:\.\d+)?)[\s,]+(-?\d+(?:\.\d+)?)/);
  if (m) return { lat: m[1], lng: m[2], formatted: `${m[1]}, ${m[2]}` };
  return null;
};
const mapsHref = (raw) => {
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const c = parseCoord(raw);
  return c ? `https://maps.google.com/?q=${c.lat},${c.lng}` : `https://maps.google.com/?q=${encodeURIComponent(raw)}`;
};

function InspectorCard({ person, isAdmin, selected, onPick }) {
  const badgeBg = isAdmin ? '#fef3c7' : '#dbeafe';
  const badgeColor = isAdmin ? '#92400e' : '#1d4ed8';
  const border = selected
    ? (isAdmin ? '#d97706' : '#2563eb')
    : (isAdmin ? '#fde68a' : '#e7e5e4');
  return (
    <button type="button" onClick={() => onPick(person)}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        width: '100%', background: '#fff', border: `1.5px solid ${border}`, borderRadius: 8,
        padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
        marginBottom: 6, minHeight: 44, transition: 'all 0.15s' }}
      data-testid={`inspector-pick-${person.user_id}`}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#1c1917' }}>{person.name}</div>
      <span style={{ background: badgeBg, color: badgeColor, padding: '3px 10px',
        borderRadius: 100, fontSize: 10, fontWeight: 800, letterSpacing: '0.5px' }}>
        {isAdmin ? 'ADMIN' : 'Staff'}
      </span>
    </button>
  );
}

export default function InspectionModal({ work, staff, onClose, onSave }) {
  const isEdit = !!work.inspection_done;
  const [step, setStep] = useState(1);
  const [inspectedBy, setInspectedBy] = useState(work.inspected_by || '');
  const [gps, setGps] = useState(work.inspection_gps || '');
  const [photos, setPhotos] = useState(work.inspection_photos || []);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const groups = groupInspectors(staff);
  const parsed = parseCoord(gps);
  const inspector = staff.find((s) => s.user_id === inspectedBy);

  const pickInspector = (p) => { setInspectedBy(p.user_id); setStep(2); };

  const onAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) {
        if (f.size > 6 * 1024 * 1024) { toast.error(`${f.name}: too large (max 6MB)`); continue; }
        const path = await uploadFile('inspection-photos', f);
        paths.push(path);
      }
      setPhotos((p) => [...p, ...paths]);
      toast.success(`${paths.length} photo(s) added`);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); e.target.value = ''; }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const save = async () => {
    if (!inspectedBy) { setStep(1); toast.error('Please select the inspector'); return; }
    if (!gps.trim()) { toast.error('Property Location (GPS) is required'); return; }
    setSaving(true);
    try {
      await onSave(work, {
        inspection_done: true,
        inspected_by: inspectedBy,
        inspection_gps: gps.trim(),
        inspection_photos: photos,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 480 }} data-testid="inspection-modal">
        <div className="modal-header">
          <h3 style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 16 }}>
            <span style={{ fontWeight: 800, color: '#1c1917' }}>Step {step} of 2 ·</span>
            <span style={{ fontWeight: 700, color: '#44403c' }}>{step === 1 ? 'Select Inspector' : 'Property Location'}</span>
          </h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {step === 1 && (
          <div data-testid="step-inspector">
            <p style={{ fontSize: 13, color: '#57534e', marginBottom: 16, lineHeight: 1.4 }}>
              Who carried out the site inspection? Tap their name to continue.
            </p>
            {groups.map((g) => {
              const Icon = g.icon;
              return (
                <div key={g.key} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Icon style={{ width: 14, height: 14, color: g.isAdmin ? '#92400e' : '#57534e' }} />
                    <span style={{ fontSize: 11, fontWeight: 800, color: g.isAdmin ? '#92400e' : '#44403c', letterSpacing: '1.2px' }}>{g.label}</span>
                    <span style={{ background: g.isAdmin ? '#fef3c7' : '#e7e5e4', color: g.isAdmin ? '#92400e' : '#44403c',
                      padding: '1px 8px', borderRadius: 100, fontSize: 10, fontWeight: 700 }}>{g.items.length}</span>
                  </div>
                  {g.items.map((p) => (
                    <InspectorCard key={p.user_id} person={p} isAdmin={g.isAdmin}
                      selected={inspectedBy === p.user_id} onPick={pickInspector} />
                  ))}
                </div>
              );
            })}
            <button type="button" onClick={onClose}
              style={{ width: '100%', padding: 14, borderRadius: 10, border: '1.5px solid #d6d3d1',
                background: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                color: '#44403c', marginTop: 4 }}
              data-testid="inspector-cancel">Cancel</button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-location">
            {/* Selected inspector pill + change link */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#f5f5f4', border: '1px solid #e7e5e4', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 10, color: '#78716c', fontWeight: 700, letterSpacing: '0.5px' }}>INSPECTOR</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1c1917' }}>{inspector?.name || '—'}</div>
              </div>
              <button onClick={() => setStep(1)}
                style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer',
                  fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}
                data-testid="change-inspector"><ArrowLeft style={{ width: 12, height: 12 }} /> Change</button>
            </div>

            {/* PROPERTY LOCATION */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <MapPin style={{ width: 16, height: 16, color: '#dc2626' }} />
              <span style={{ fontFamily: 'var(--font-title)', fontSize: 13, fontWeight: 800, color: '#1c1917', letterSpacing: '0.5px' }}>PROPERTY LOCATION</span>
              <span style={{ color: '#dc2626', fontWeight: 700 }}>*</span>
            </div>

            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px',
              fontSize: 12, lineHeight: 1.5, color: '#7c2d12', marginBottom: 10 }}>
              <strong>How to:</strong> Tap "Open Google Maps" → long-press on the property → copy the coordinates → paste below
            </div>

            <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: '#1d4ed8', color: '#fff', padding: '12px 14px', borderRadius: 8,
                fontWeight: 800, fontSize: 14, textDecoration: 'none', marginBottom: 10, minHeight: 44 }}
              data-testid="open-google-maps">
              <Globe style={{ width: 18, height: 18 }} /> Open Google Maps
            </a>

            <input className="field-input" placeholder="10.069832, 78.783452"
              value={gps} onChange={(e) => setGps(e.target.value)}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 14, padding: '12px 14px' }}
              data-testid="insp-gps" />

            {parsed && (
              <div style={{ marginTop: 10, background: '#dcfce7', border: '1.5px solid #86efac', borderRadius: 8, padding: '12px 14px' }} data-testid="gps-confirmed">
                <div style={{ fontSize: 13, fontWeight: 800, color: '#15803d', marginBottom: 4 }}>✓ Property Location Set</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: '#14532d', marginBottom: 6 }}>{parsed.formatted}</div>
                <a href={mapsHref(gps)} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#15803d',
                    fontWeight: 700, fontSize: 12, textDecoration: 'underline' }}
                  data-testid="verify-on-maps">
                  <MapPin style={{ width: 12, height: 12 }} /> Verify on Maps <ExternalLink style={{ width: 10, height: 10 }} />
                </a>
              </div>
            )}

            {/* PROPERTY PHOTOS (optional) */}
            <div className="field" style={{ marginTop: 16 }}>
              <label className="field-label">Property Photos <span style={{ color: '#78716c', fontWeight: 500 }}>(optional · {photos.length} added)</span></label>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '4px 0', marginBottom: 4 }}>
                {photos.map((p, i) => (
                  <div key={p || `pphoto-${i}`} style={{ position: 'relative', flex: 'none' }}>
                    <img src={srcForPath(p)} alt={`p${i}`}
                      style={{ width: 78, height: 78, objectFit: 'cover', borderRadius: 6, border: '1px solid #e7e5e4' }}
                      data-testid={`insp-thumb-${i}`} />
                    <button type="button" onClick={() => removePhoto(i)}
                      style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%',
                        background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                  </div>
                ))}
                <label style={{ flex: 'none', width: 78, height: 78, border: '2px dashed #d6d3d1', borderRadius: 6,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#78716c', fontSize: 10, fontWeight: 600 }} data-testid="insp-upload">
                  {busy ? <span>…</span> : (<><Upload style={{ width: 18, height: 18 }} /><span>Add</span></>)}
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onAddPhotos} disabled={busy} />
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={saving || busy} data-testid="save-insp-btn">
                {saving ? 'Saving…' : isEdit ? 'Save Changes' : <><Check style={{ width: 14, height: 14 }} /> Mark Done</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
